import { Chip, Tooltip } from "@mui/material";
import Typography from "@mui/material/Typography";
import { batch, useSignal, useSignalEffect } from "@preact/signals-react";
import { isArray, isNil, isObject, isString, set } from "lodash-es";
import { Label } from "../../../resolvers-types";
import { publishUserAction } from "../../library/analytics";
import {
  getDefaultCCY,
  GetFormatFromSite,
  getLocale,
  getScope,
  getState,
  setupHandlers,
  setupLocalState,
} from "../../library/dataService";
import { getExprValue } from "../../library/interpreter";
import { log } from "../../library/logger";
import { INGLabelProps } from "../../library/NGFieldExtensions";
import {
  cloneDeepWithoutSelectedFields,
  formatDate,
  formatNumber,
  getClassName,
  getCustomLabel,
  getFirstNonEmpty,
  getsxObject,
  getTestId,
  isNullOrEmpty,
  partitionMUIClasses,
} from "../../library/utils";
import NGIcon from "../NGIcon/NGIcon";

const tag = "NGLabel";

function hasConditionalFormat(format: any) {
  // TODO VS: properly type format
  return !isNil(format.ConditionalFormatBindings) && !isNil(format.ConditionalFormats);
}

function enrichConfig(config: any, context: any): any {
  if (isNil(config.FormatName) && isNil(config?.Bindings?.FormatName)) return config;
  let formatName = config.FormatName;

  if (config?.Bindings?.FormatName) {
    const { state, form, parentState } = getState(context);
    const scope = getScope(context, config, state, form, {}, parentState);
    formatName = getExprValue(config.Bindings.FormatName, scope, null);
  }

  const format = GetFormatFromSite(formatName);

  if (isNil(format)) return config;

  const a = cloneDeepWithoutSelectedFields(format, ["ConditionalFormatBindings", "ConditionalFormats", "Name", "Id"]);

  return { ...config, ...a };
}

export default function NGLabel({ config, context }: INGLabelProps) {
  config = enrichConfig(config, context);
  const testId = getTestId(config);

  // if (!isNil(config.StartIcon)) config.StartIcon.ContextId = testId;
  // if (!isNil(config.EndIcon)) config.EndIcon.ContextId = testId;

  const local = setupLocalState(
    config,
    {
      Value: useSignal(config.Value ?? config.DefaultValue ?? ""),
      Visible: useSignal(config.Visible != false ? true : config.Visible),
      Style: useSignal(config.Style ?? {}),
      Classes: useSignal(config.Classes ?? []),
      ChipClasses: useSignal(config.ChipClasses ?? []),
      StartIcon: useSignal(config.StartIcon ?? {}),
      EndIcon: useSignal(config.EndIcon ?? {}),
      Currency: useSignal(config.Currency ?? ""),
      Label: useSignal(config.Label ?? ""),
      Title: useSignal(config.Title ?? ""),
    },
    context
  );

  useSignalEffect(() => {
    if (isNil(config.FormatName)) return;

    const format = GetFormatFromSite(config.FormatName);

    if (isNil(format) || !hasConditionalFormat(format)) return;

    function getMatch() {
      const { form } = getState(context);
      for (const [k, v] of Object.entries(format.ConditionalFormatBindings)) {
        const scope = { Data: { Value: local.Value.value }, Form: form };
        const srcValue = getExprValue(v as any, scope, null);

        if (srcValue === true) {
          return k;
        }

        // console.log("srcValue", srcValue, formCtx);
      }
    }

    const matchedProperty = getMatch();

    if (isNil(matchedProperty) || isNil(format.ConditionalFormats[matchedProperty])) return;

    batch(() => {
      for (const [k, v] of Object.entries(format.ConditionalFormats[matchedProperty])) {
        if (isNil(local[k])) {
          log.error(tag, `The format property '${k}' cannot be dynamically bound to a label`);
          continue;
        }

        if (isObject(v)) local[k].value = { ...config[k], ...v, IgnoreLocalState: true };
      }
    });

    //console.log("Selected Format", format.ConditionalFormats[matchedProperty]);
  });

  config.getData = () => {
    return local.Value.value;
  };

  function getFormattedLabel(v) {
    if (isNullOrEmpty(v)) return "";

    if (!isNil(config.NumberFormat)) {
      const ccy = isNullOrEmpty(local.Currency.value) ? getDefaultCCY() : local.Currency.value;
      return formatNumber(getLocale(), ccy, v, config.NumberFormat);
    }

    if (!isNil(config.DateFormat)) return formatDate(getLocale(), v, config.DateFormat);

    return v;
  }

  // useSignalEffect(()=>{

  // })

  // if (Array.isArray(local.Value.value)) {
  //   return local.Value.value.map((v, i) => {
  //     return <Chip label={v} key={i} />;
  //   });

  //   const sep = config.ListSeparator ?? ",";
  //   return local.Value.value.join(sep);
  // }

  const isArrayValue: boolean = Array.isArray(local.Value.value);
  const isObjectValue: boolean = !isArrayValue && isObject(local.Value.value);

  const handlers = setupHandlers(config, context);

  function setStyleMultiline(local: any) {
    set(local.Style.value, "display", "-webkit-box");
    set(local.Style.value, "WebkitBoxOrient", "vertical");
    set(local.Style.value, "overflow", "hidden");

    set(local.Style.value, "WebkitLineClamp", config.NumberOfLines ?? 1000);
    set(local.Style.value, "boxSizing", "border-box"); // Include padding and border in the element's total width and height
  }

  function setStyleEllipsis(local: any) {
    if (!config.MultiLine) set(local.Style.value, "whiteSpace", "nowrap");

    set(local.Style.value, "overflow", "hidden");
    set(local.Style.value, "textOverflow", "ellipsis");
  }

  function getLabelStart(
    v: string,
    config: Label,
    local: any,
    getFormattedLabel: (v) => any,
    key: number | undefined,
    addTestId: boolean
  ) {
    // if (isNullOrEmpty(v)) return null;

    if (config.DisplayAsChip && v) {
      return (
        <Chip
          className={getClassName(local.ChipClasses)}
          sx={{ margin: "3px" }}
          key={key}
          label={getLabelWithTooltip(v, config, local, getFormattedLabel, false)}
        />
      );
    }

    return getLabelWithTooltip(v, config, local, getFormattedLabel, addTestId);
  }

  function getLabelWithTooltip(
    v: string,
    config: Label,
    local: any,
    getFormattedLabel: (v) => any,
    addTestId: boolean
  ) {
    if (config.EllipsisOnOverflow) {
      setStyleEllipsis(local);
      return (
        <Tooltip
          data-testid={addTestId ? getTestId(config) : undefined}
          data-type={addTestId ? config.__typename : undefined}
          title={getFormattedLabel(v)}
        >
          {getLabel(v, local, getFormattedLabel, false)}
        </Tooltip>
      );
    }

    if (local.Title.value) {
      return (
        <Tooltip title={getFormattedLabel(local.Title.value)}>
          {getLabel(v, local, getFormattedLabel, addTestId)}
        </Tooltip>
      );
    }

    return getLabel(v, local, getFormattedLabel, addTestId);
  }

  function getLabel(v, local: any, getFormattedLabel: (v) => any, addTestId: boolean) {
    if (config.MultiLine) setStyleMultiline(local);

    const s = getsxObject(local.Style.value, {
      display: "flex",
      alignItems: "center",
    });

    v = getCustomLabel(v);
    return (
      <Typography
        id={config.SetAsBookmark ? config.Id : undefined}
        data-testid={addTestId ? getTestId(config) : undefined}
        data-type={addTestId ? config.__typename : undefined}
        sx={s}
        className={getClassName(local.Classes, context)}
        onClick={(e) => {
          const [_, classes] = partitionMUIClasses(local.Classes.value);
          const action = publishUserAction(config, context, "label_click", e, {
            ElementText: getFirstNonEmpty(
              local.Label.value,
              local.Title.value,
              config.Icon?.IconName,
              config.StartIcon?.IconName,
              config.EndIcon?.IconName
            ),
            EventCategory: "engagement",
            ElementClass: classes.join(" "),
          });
          handlers["onClick"]?.(e, null, action);
        }}
      >
        {local.StartIcon.value && !isNullOrEmpty(local.StartIcon.value.IconName) && (
          <NGIcon config={{ ...local.StartIcon.value, ContextId: testId }} context={context} />
        )}
        {getFormattedLabel(v)}
        {local.EndIcon.value && !isNullOrEmpty(local.EndIcon.value.IconName) && (
          <NGIcon config={{ ...local.EndIcon.value, ContextId: testId }} context={context} />
        )}
      </Typography>
    );
  }

  const valueAsChip = isArray(local.Value.value)
    ? local.Value.value
    : isString(local.Value.value)
    ? local.Value.value.split(",")
    : [];

  return (
    <>
      {isObjectValue && "This label is an object, which is not supported.  Correct your binding."}
      {local.Visible.value && !isObjectValue && config.DisplayAsChip && (
        <div data-testid={getTestId(config)} data-type={config.__typename}>
          {valueAsChip?.map((v, i) => {
            return getLabelStart(v, config, local, getFormattedLabel, i, false);
          })}
        </div>
      )}
      {local.Visible.value &&
        !isObjectValue &&
        !isArrayValue &&
        !config.DisplayAsChip &&
        getLabelStart(
          isArrayValue ? local.Value.value.join(config.ListSeparator ?? ",") : local.Value.value,
          config,
          local,
          getFormattedLabel,
          undefined,
          true
        )}
    </>
  );
}
